/**
 * Group Schedule Filter (V060)
 *
 * Decides if a PlaylistItem can play RIGHT NOW based on the schedule(s) of
 * the player's groups that originated the item. Mirrors `contentScheduleFilter`
 * pra reusar primitivas de `scheduleParser.ts` (preserva normalização Tizen
 * hour=24 — v1.0.105+).
 *
 * Architecture (decisão Renato — Socratic Gate V060):
 *
 *   Server (`MediaSyncService.resolveProgramacoes`):
 *     TIER 1 (associação direta) → `viaDireto=true` no item.
 *     TIER 2 (via grupo) → `viaGrupos=[id1, id2]` no item.
 *     SyncResponse traz `playerGroups[]` com a janela de cada grupo a que o
 *     player pertence (mesmo formato de schedule de Conteúdo/Campanha).
 *
 *   Cliente (este arquivo):
 *     - BYPASS: `viaDireto=true` → ignora a janela do grupo (associação direta
 *       = "via expressa"). Operador escolheu este player → toca mesmo fora
 *       do horário do grupo.
 *     - UNIÃO: `viaGrupos=[id1, id2]` → toca se QUALQUER um dos grupos
 *       referenciados permitir o horário atual (mais permissivo).
 *     - Sem `viaGrupos` (TIER 3+ / fallback) → sem filtro de grupo, segue
 *       composição AND com Conteúdo/Campanha/PlaylistItem.
 *     - Fail-open: `viaGrupos` apontando pra grupo que NÃO está em
 *       `playerGroups[]` (caso degradado) → trata como sem restrição.
 *
 *   Composição AND completa no player (4 camadas):
 *     Conteúdo (V059) ∩ PlaylistItem ∩ Campanha ∩ Grupo (V060)
 */

import { PlayerGroupSchedule, PlaylistItem } from '../types';
import {
  getCurrentTimeInTimezone,
  isDayActive,
  isTimeInRange,
} from './scheduleParser';

const WEEKDAY_CODE_TO_INDEX: Record<string, number> = {
  SUN: 0,
  MON: 1,
  TUE: 2,
  WED: 3,
  THU: 4,
  FRI: 5,
  SAT: 6,
};

const WEEKDAY_INDEX_TO_CODE: Record<number, string> = {
  0: 'SUN',
  1: 'MON',
  2: 'TUE',
  3: 'WED',
  4: 'THU',
  5: 'FRI',
  6: 'SAT',
};

const DEFAULT_TZ = 'America/Sao_Paulo';

export interface GroupScheduleDecision {
  /** true if item can play RIGHT NOW under group window rules. */
  isPlayable: boolean;
  /** null if isPlayable=true; otherwise human-friendly reason (debug/log). */
  reason: string | null;
  /** Group id(s) that PERMITIRAM a passagem (any-pass na UNIÃO). Pode ser []
   *  quando isPlayable=true por BYPASS direto ou por fail-open. */
  passedByGroups: number[];
}

function normalizeWeekdayCode(raw: string): string {
  return raw.trim().toUpperCase().slice(0, 3);
}

/**
 * Same SIMPLE/ADVANCED machinery used pra Conteúdo (V059) e Campanha — mas
 * aplicada a um único grupo.
 *
 * <p>2026-06-12 — `validFrom`/`validUntil` agora SÃO avaliados aqui no
 * fuso LOCAL do device (decisão Renato — R0 em regras-temporais-player.md).
 * Grupo definido com "até 12/06" expira no fim do dia 12/06 LOCAL do
 * player, não em BRT.</p>
 */
function isGroupCurrentlyOpen(
  schedule: PlayerGroupSchedule,
  now: Date,
): { open: boolean; reason: string | null } {
  // EARLY RETURN — validade no fuso LOCAL do device (vide R0).
  if (schedule.validFrom) {
    const start = parseLocalStartOfDay(schedule.validFrom);
    if (start && now < start) {
      return {
        open: false,
        reason: `validFrom ${schedule.validFrom} ainda no futuro (fuso local)`,
      };
    }
  }
  if (schedule.validUntil) {
    const end = parseLocalEndOfDay(schedule.validUntil);
    if (end && now > end) {
      return {
        open: false,
        reason: `validUntil ${schedule.validUntil} já passou (fuso local)`,
      };
    }
  }

  // Normaliza `scheduleMode` — server envia uppercase, cliente compara igual.
  const mode = schedule.scheduleMode;

  // ADVANCED mode — uses weeklySchedule
  if (mode === 'ADVANCED') {
    const weekly = schedule.weeklySchedule;
    if (!weekly || Object.keys(weekly).length === 0) {
      return { open: false, reason: 'ADVANCED sem weeklySchedule' };
    }
    const { hours, minutes, dayOfWeek } = getCurrentTimeInTimezone(
      DEFAULT_TZ,
      now,
    );
    const todayCode = WEEKDAY_INDEX_TO_CODE[dayOfWeek];
    const dayConfig = weekly[todayCode];
    if (!dayConfig) {
      return {
        open: false,
        reason: `dia ${todayCode} não configurado em weeklySchedule`,
      };
    }
    try {
      const inRange = isTimeInRange(
        { hours, minutes },
        dayConfig.startAt,
        dayConfig.endAt,
      );
      return {
        open: inRange,
        reason: inRange
          ? null
          : `hora ${hours}:${minutes} fora de ${dayConfig.startAt}-${dayConfig.endAt} (${todayCode})`,
      };
    } catch (err) {
      return {
        open: false,
        reason: `weeklySchedule[${todayCode}] inválido: ${(err as Error).message}`,
      };
    }
  }

  // SIMPLE mode (default) — daysOfWeek (lowercase) + startAt/endAt.
  const daysRaw = schedule.daysOfWeek;
  const startAt = schedule.startAt;
  const endAt = schedule.endAt;

  const hasDayFilter = daysRaw && daysRaw.length > 0;
  const hasTimeFilter =
    !!startAt && !!endAt && !(startAt === '00:00' && endAt === '23:59');

  // Grupo sem nenhum filtro = sempre aberto (sem restrição horária definida).
  if (!hasDayFilter && !hasTimeFilter) {
    return { open: true, reason: null };
  }

  const { hours, minutes, dayOfWeek } = getCurrentTimeInTimezone(
    DEFAULT_TZ,
    now,
  );

  if (hasDayFilter) {
    const dayIndices = daysRaw!
      .map((d) => WEEKDAY_CODE_TO_INDEX[normalizeWeekdayCode(d)])
      .filter((idx) => idx !== undefined);
    if (!isDayActive(dayOfWeek, dayIndices)) {
      const todayCode = WEEKDAY_INDEX_TO_CODE[dayOfWeek];
      return {
        open: false,
        reason: `dia ${todayCode} ∉ daysOfWeek=[${daysRaw!.join(',')}]`,
      };
    }
  }

  if (hasTimeFilter) {
    try {
      const inRange = isTimeInRange({ hours, minutes }, startAt!, endAt!);
      if (!inRange) {
        return {
          open: false,
          reason: `hora ${hours}:${minutes} fora de ${startAt}-${endAt}`,
        };
      }
    } catch (err) {
      return {
        open: false,
        reason: `startAt/endAt inválido: ${(err as Error).message}`,
      };
    }
  }

  return { open: true, reason: null };
}

/**
 * Decide se um item pode tocar AGORA respeitando regras de grupo (V060).
 *
 * Ordem de avaliação:
 *  1. BYPASS: `viaDireto === true` → SEMPRE toca (ignora grupos).
 *  2. Item sem `viaGrupos` (TIER 3+) → SEMPRE toca (sem filtro de grupo).
 *  3. Tem `viaGrupos`: UNIÃO — toca se QUALQUER grupo referenciado permitir.
 *  4. Fail-open: se nenhum dos `viaGrupos` está em `playerGroups[]`, trata
 *     como sem restrição (caso degradado — sync inconsistente).
 */
export function isGroupSchedulePlayable(
  item: PlaylistItem,
  playerGroups: PlayerGroupSchedule[] | null | undefined,
  now: Date = new Date(),
): GroupScheduleDecision {
  // 1. BYPASS via associação direta — ignora janela do grupo.
  if (item.viaDireto === true) {
    return {
      isPlayable: true,
      reason: null,
      passedByGroups: [],
    };
  }

  const viaGrupos = item.viaGrupos;

  // 2. Sem viaGrupos → sem filtro (TIER 3+, fallback).
  if (!viaGrupos || viaGrupos.length === 0) {
    return {
      isPlayable: true,
      reason: null,
      passedByGroups: [],
    };
  }

  // 3+4. Tem viaGrupos: localiza os schedules e aplica UNIÃO.
  const groupsById = new Map<number, PlayerGroupSchedule>();
  if (playerGroups) {
    for (const g of playerGroups) {
      groupsById.set(g.id, g);
    }
  }

  const knownGroups = viaGrupos
    .map((id) => groupsById.get(id))
    .filter((g): g is PlayerGroupSchedule => !!g);

  // Fail-open: viaGrupos referencia grupos não-presentes no sync.
  if (knownGroups.length === 0) {
    return {
      isPlayable: true,
      reason: `fail-open (viaGrupos=[${viaGrupos.join(',')}] não estão em playerGroups)`,
      passedByGroups: [],
    };
  }

  // UNIÃO: toca se PELO MENOS UM grupo estiver aberto agora.
  const passReasons: string[] = [];
  const passedByGroups: number[] = [];
  for (const g of knownGroups) {
    const { open, reason } = isGroupCurrentlyOpen(g, now);
    if (open) {
      passedByGroups.push(g.id);
    } else {
      passReasons.push(
        `grupo#${g.id}${g.name ? `(${g.name})` : ''}: ${reason}`,
      );
    }
  }

  if (passedByGroups.length > 0) {
    return {
      isPlayable: true,
      reason: null,
      passedByGroups,
    };
  }

  return {
    isPlayable: false,
    reason: `nenhum grupo aberto agora — ${passReasons.join(' | ')}`,
    passedByGroups: [],
  };
}

/**
 * 2026-06-12 — Parse "YYYY-MM-DD" no fuso LOCAL do device. Helpers
 * compartilhados com `playerScheduleFilter.ts` (mesma lógica, copiados
 * pra manter cada filter self-contained).
 */
function parseLocalStartOfDay(dateStr: string): Date | null {
  const parts = dateStr.split('-').map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  return new Date(parts[0], parts[1] - 1, parts[2], 0, 0, 0, 0);
}

function parseLocalEndOfDay(dateStr: string): Date | null {
  const parts = dateStr.split('-').map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  return new Date(parts[0], parts[1] - 1, parts[2], 23, 59, 59, 999);
}
